import React from 'react';
import KanbanCard from '../KanbanCard';
import { ArrowRight, X } from 'lucide-react';

// Logos
import googleAdsLogo from '../../../logos/google-ads.png';
import microsoftAdsLogo from '../../../logos/microsoft-ads.png';
import metaAdsLogo from '../../../logos/meta-ads.png';
import pianoLogo from '../../../logos/piano-analytics.png';
import matomoLogo from '../../../logos/matomo.png';
import searchConsoleLogo from '../../../logos/google-search-console.png';
import tagManagerLogo from '../../../logos/google-tag-manager.png';
import merchantLogo from '../../../logos/google-merchant.png';
import googleAnalyticsLogo from '../../../logos/google-analytics.svg';
import pinterestAdsLogo from '../../../logos/pinterest-ads.png';
import tiktokAdsLogo from '../../../logos/tiktok-ads.png';
import chatgptAdsLogo from '../../../logos/chatgpt-ads.png';
import awinLogo from '../../../logos/awin.png';
import prestashopLogo from '../../../logos/prestashop.png';

// Config par provider
const providerConfig = {
  GOOGLE_ADS: { label: 'Google Ads', logo: googleAdsLogo },
  GOOGLE_SHEETS: { label: 'Sheets', logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-drive.png' },
  GOOGLE_MERCHANT_API: { label: 'Google Merchant Center', logo: merchantLogo },
  GOOGLE_ANALYTICS: { label: 'Google Analytics', logo: googleAnalyticsLogo },
  GOOGLE_SEARCH_CONSOLE: { label: 'Google Search Console', logo: searchConsoleLogo },
  GOOGLE_TAG_MANAGER: { label: 'Google Tag Manager', logo: tagManagerLogo },
  MICROSOFT_ADS: { label: 'Microsoft', logo: microsoftAdsLogo },
  FACEBOOK_ADS: { label: 'Meta', logo: metaAdsLogo },
  MATOMO: { label: 'Matomo', logo: matomoLogo },
  PIANO_ANALYTICS: { label: 'Piano', logo: pianoLogo },
  PINTEREST_ADS: { label: 'Pinterest', logo: pinterestAdsLogo },
  TIKTOK_ADS: { label: 'TikTok', logo: tiktokAdsLogo },
  OPENAI_ADS: { label: 'ChatGPT Ads', logo: chatgptAdsLogo },
  AWIN: { label: 'Awin', logo: awinLogo },
  PRESTASHOP: { label: 'PrestaShop', logo: prestashopLogo }
};

const AccountCard = ({ account, onDissociate, onClick, isSelected = false }) => {
  const config = providerConfig[account.provider] || { label: account.provider, logo: null };
  const isAssociated = !!account.siteId;

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.375rem'
  };

  const logoStyle = {
    width: '18px',
    height: '18px',
    objectFit: 'contain',
    flexShrink: 0
  };

  const providerLabelStyle = {
    fontSize: '0.7rem',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    fontWeight: '500'
  };

  const nameStyle = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '0.25rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const sourceIdStyle = {
    fontSize: '0.75rem',
    color: '#94a3b8',
    fontFamily: 'monospace',
    marginBottom: '0.5rem',
    // Même traitement que le nom juste au-dessus. Les identifiants ChatGPT Ads font
    // 39 caractères (`adacct_` + 32 hexadécimaux), près du double d'un identifiant Meta :
    // sans troncature ils débordent de la carte.
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const associationStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.375rem 0.5rem',
    backgroundColor: isAssociated ? '#f0fdf4' : '#f3efe7',
    borderRadius: '0.375rem',
    marginTop: '0.5rem'
  };

  const associationTextStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    fontSize: '0.75rem',
    color: isAssociated ? '#16a34a' : '#94a3b8'
  };

  const dissociateButtonStyle = {
    padding: '0.125rem',
    borderRadius: '0.25rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#94a3b8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  };

  const cardWrapperStyle = {
    marginBottom: '0.25rem'
  };

  return (
    <div style={cardWrapperStyle}>
      <KanbanCard
      id={account.id || account.sourceId}
      type="account"
      data={account}
      draggable={true}
      onClick={onClick}
      isSelected={isSelected}
    >
      <div>
        <div style={headerStyle}>
          {config.logo && (
            <img src={config.logo} alt={config.label} style={logoStyle} />
          )}
          <span style={providerLabelStyle}>{config.label}</span>
        </div>

        <div style={nameStyle} title={account.name}>
          {account.name || 'Compte sans nom'}
        </div>

        {account.sourceId && (
          <div style={sourceIdStyle} title={account.sourceId}>
            {account.sourceId}
          </div>
        )}

        <div style={associationStyle}>
          <div style={associationTextStyle}>
            <ArrowRight size={12} />
            {isAssociated ? (
              <span>{account.siteName}</span>
            ) : (
              <span style={{ fontStyle: 'italic' }}>Non associé</span>
            )}
          </div>

          {isAssociated && onDissociate && (
            <button
              style={dissociateButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                onDissociate(account.id || account.sourceId, account.siteId);
              }}
              title="Dissocier du site"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fee2e2';
                e.currentTarget.style.color = '#dc2626';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
              }}
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>
    </KanbanCard>
    </div>
  );
};

export default AccountCard;
